export type OfficePaymentStatus = "unpaid" | "paid";
export type TalentPaymentStatus =
  | "not_started"
  | "processing"
  | "action_required"
  | "paid";

export interface ClaimPaymentResult {
  httpStatus: number;
  paymentLifecycleId?: number | null;
  officePaymentStatus: OfficePaymentStatus;
  talentPaymentStatus: TalentPaymentStatus;
  talentPaymentMethod?: string | null;
  paymentStatusRefreshRequired?: boolean;
}

export interface ClaimPaymentOutcome {
  note: string;
  message: string;
  shouldResolve: boolean;
  requiresAction: boolean;
}

export const UNKNOWN_OFFICE_PAYMENT_MESSAGE =
  "Payment outcome could not be confirmed. Do not charge again or send a replacement payment; refresh and contact Support.";

export function getOfficeChargeFailureMessage(error: any): string {
  const status = Number(error && (error.status || (error.response && error.response.status)));
  const message = error && typeof error.message === "string" ? error.message.trim() : "";
  if (status >= 400 && status < 500 && status !== 408 && message
    && error.paymentOutcome !== "unknown"
    && !/reconciliation|outcome.*unknown|outcome could not|^HTTP error!/i.test(message)) {
    return message;
  }
  return UNKNOWN_OFFICE_PAYMENT_MESSAGE;
}

export function getClaimPaymentOutcome(
  result: ClaimPaymentResult
): ClaimPaymentOutcome {
  if (result.officePaymentStatus !== "paid") {
    throw new Error("Office payment was not confirmed");
  }

  if (result.paymentStatusRefreshRequired) {
    return {
      note: "office payment received; payout status refresh required",
      message: "Office payment received; Talent payout status could not be refreshed. Do not charge again or send a replacement payment; refresh and contact Support.",
      shouldResolve: false,
      requiresAction: true
    };
  }

  if (result.talentPaymentStatus === "paid") {
    return {
      note: "payment success",
      message: "Office payment received; talent payout is complete",
      shouldResolve: true,
      requiresAction: false
    };
  }

  if (result.talentPaymentStatus === "processing") {
    return {
      note: "office payment received; talent payout processing",
      message: "Office payment received; talent payout is processing",
      shouldResolve: true,
      requiresAction: false
    };
  }

  return {
    note: "office payment received; talent payout action required",
    message: "Office payment received; talent payout needs manual action",
    shouldResolve: false,
    requiresAction: true
  };
}
